import { useMemo } from 'react';

import { FilterFieldConfig } from '../../../../ui/filters-sidebar/types';
import { FilterChangeHandlers } from '../../../../utils/list-filters/list-filter-types';
import { countActiveFilters } from '../../../../utils/list-filters/list-filter-utils';
import { useFiltersSidebar as useGenericFiltersSidebar } from '../../../../utils/list-filters/use-filters-sidebar';
import { CASFilters, CASRangeFilterKey, CASDateRangeFilterKey } from './types';
import { DEFAULT_FILTERS } from './constants';
import { useFilterConfig } from './useFilterConfig';
import { useContactOptions } from './useContactOptions';
import { TransformedContact } from './types';

const CAS_RANGE_FILTER_KEYS: readonly (CASRangeFilterKey | CASDateRangeFilterKey)[] = [
  'totalDue',
  'totalOverdue',
  'totalInvoiced',
  'invoicesDue',
  'averageSale',
  'daysToPay',
  'dependence',
  'lastActivity'
];

interface UseFiltersSidebarParams {
  allData: TransformedContact[];
  currencySymbol: string;
}

interface UseFiltersSidebarReturn {
  filters: CASFilters;
  setFilters: (filters: CASFilters) => void;
  activeFilterCount: number;
  isFiltersSidebarOpen: boolean;
  openFiltersSidebar: () => void;
  closeFiltersSidebar: () => void;
  clearAllFilters: () => void;
  draftFilters: CASFilters;
  setDraftFilters: (filters: CASFilters) => void;
  applyDraftFilters: () => void;
  cancelDraftFilters: () => void;
  filterConfig: FilterFieldConfig[];
  contactOptions: { value: string; text: string }[];
  handlers: FilterChangeHandlers;
}

/**
 * Custom hook to manage filters sidebar state and logic for the Customers and Suppliers list
 * @param allData - The full list of contacts to derive filter options from
 * @param currencySymbol - The currency symbol to display in range filter labels
 * @returns An object containing the current filter state, draft filter state, active filter keys, and handlers for managing the filters sidebar
 */
export const useFiltersSidebar = ({ allData, currencySymbol }: UseFiltersSidebarParams): UseFiltersSidebarReturn => {
  const sidebar = useGenericFiltersSidebar<CASFilters>({
    defaultFilters: DEFAULT_FILTERS,
    rangeFilterKeys: CAS_RANGE_FILTER_KEYS
  });

  const contactOptions = useContactOptions(allData);

  const filterConfig = useFilterConfig({
    contactOptions,
    draftFilters: sidebar.draftFilters,
    activeFilterKeys: sidebar.activeFilterKeys,
    currencySymbol
  });

  const activeFilterCount = useMemo(
    () =>
      countActiveFilters({
        textMatchFilters: { contacts: sidebar.filters.contacts },
        rangeFilters: {
          totalDue: sidebar.filters.totalDue,
          totalOverdue: sidebar.filters.totalOverdue,
          totalInvoiced: sidebar.filters.totalInvoiced,
          invoicesDue: sidebar.filters.invoicesDue,
          averageSale: sidebar.filters.averageSale,
          daysToPay: sidebar.filters.daysToPay,
          dependence: sidebar.filters.dependence
        },
        dateRangeFilters: { lastActivity: sidebar.filters.lastActivity }
      }),
    [sidebar.filters]
  );

  return {
    filters: sidebar.filters,
    setFilters: sidebar.setFilters,
    activeFilterCount,
    isFiltersSidebarOpen: sidebar.isOpen,
    openFiltersSidebar: sidebar.open,
    closeFiltersSidebar: sidebar.close,
    clearAllFilters: sidebar.clearAll,
    draftFilters: sidebar.draftFilters,
    setDraftFilters: sidebar.setDraftFilters,
    applyDraftFilters: sidebar.apply,
    cancelDraftFilters: sidebar.cancel,
    filterConfig,
    contactOptions,
    handlers: sidebar.handlers
  };
};
